/**
 * Sales Legal — LOI & Agreement queues for the signed-in user's clients.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
    Search, RefreshCw, Eye, Plus, ArrowUp, ArrowDown,
    FileCheck2, FileSignature,
} from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../utils/api';
import { useSettings } from '../../store/settings';
import { useAuth } from '../../store/auth';
import { useRoleDoc } from '../../utils/useRoleDoc';
import { canUpdateLoiStage, canUpdateAgreementStage } from '../../utils/workspace';
import {
    BUCKET_META,
    enrichLegalRow,
    getActiveStages,
} from '../../utils/legalWorkflow';

const inr = (v) =>
    new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 })
        .format(Number(v || 0));

const TABS = [
    { id: 'loi', label: 'LOI', icon: FileCheck2 },
    { id: 'agreement', label: 'Agreement', icon: FileSignature},
];

const STAT_KEYS = ['all', 'not_sent', 'requested', 'rejected', 'submitted'];

export default function SalesLegal() {
    const user = useAuth(s => s.user);
    const roleDoc = useRoleDoc(user);
    const paymentSettings = useSettings(s => s.paymentSettings);
    const legal = useSettings(s => s.legal);

    const [tab, setTab] = useState('loi');
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [sortBy, setSortBy] = useState({ key: 'name', dir: 'asc' });
    const [busyId, setBusyId] = useState(null);

    const canUpdateLoi = canUpdateLoiStage(user, roleDoc);
    const canUpdateAgreement = canUpdateAgreementStage(user, roleDoc);
    const loiStages = useMemo(() => getActiveStages(legal?.loiStages), [legal?.loiStages]);
    const agreementStages = useMemo(() => getActiveStages(legal?.agreementStages), [legal?.agreementStages]);

    const load = useCallback(() => {
        setLoading(true);
        api.get('/forms/submissions/me')
            .then(r => setItems(r.data.items || []))
            .catch(() => setItems([]))
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => { load(); }, [load]);

    const baseRows = useMemo(() => {
        const workflow = tab;
        const stageList = workflow === 'loi' ? loiStages : agreementStages;

        return items
            .filter(s => s.status === 'approved' && (s.clientStatus || 'active') === 'active')
            .map(s => enrichLegalRow(s, { paymentSettings, stages: stageList, workflow }))
            .filter(row => (workflow === 'loi' ? row.loiEligible : row.agreementEligible));
    }, [items, tab, paymentSettings, loiStages, agreementStages]);

    const stats = useMemo(() => {
        const counts = { all: baseRows.length, not_sent: 0, requested: 0, rejected: 0, submitted: 0 };
        baseRows.forEach(r => { if (counts[r.bucket] != null) counts[r.bucket] += 1; });
        return counts;
    }, [baseRows]);

    const view = useMemo(() => {
        const rx = q.trim() ? new RegExp(q.trim(), 'i') : null;
        let list = baseRows.filter(r => {
            if (statusFilter !== 'all' && r.bucket !== statusFilter) return false;
            if (rx && !rx.test(r.name)) return false;
            return true;
        });

        const dir = sortBy.dir === 'asc' ? 1 : -1;
        const val = (r) => {
            switch (sortBy.key) {
                case 'deal': return r.deal;
                case 'pct': return r.pct;
                case 'balance': return r.balance;
                case 'status': return r.stageOrder;
                default: return r.name.toLowerCase();
            }
        };
        return [...list].sort((a, b) => {
            const va = val(a);
            const vb = val(b);
            if (va < vb) return -1 * dir;
            if (va > vb) return 1 * dir;
            return 0;
        });
    }, [baseRows, q, statusFilter, sortBy]);

    const setSort = (key) => setSortBy(s =>
        s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'desc' });

    const requestLoi = async (row) => {
        const requested = loiStages.find(s => s.order === 2) || loiStages[1];
        if (!requested) return toast.error('LOI stages not configured');
        setBusyId(row.id);
        try {
            await api.patch(`/forms/submissions/${row.id}/loi-stage`, { stageOrder: requested.order, notes: 'Requested from Legal page' });
            toast.success('LOI request recorded');
            load();
        } catch { /* toasted */ }
        finally { setBusyId(null); }
    };

    const requestAgreement = async (row) => {
        const requested = agreementStages.find(s => s.order === 2) || agreementStages[1];
        if (!requested) return toast.error('Agreement stages not configured');
        setBusyId(row.id);
        try {
            await api.patch(`/forms/submissions/${row.id}/agreement-stage`, { stageOrder: requested.order, notes: 'Requested from Legal page' });
            toast.success('Agreement request recorded');
            load();
        } catch { /* toasted */ }
        finally { setBusyId(null); }
    };

    const activeTab = TABS.find(t => t.id === tab);

    return (
        <div className="space-y-6 max-w-6xl">
            {/* Header */}
            <div className="flex items-start justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-extrabold text-ink-900">Legal</h1>
                </div>
                <button type="button" onClick={load} className="ag-btn-ghost shrink-0" disabled={loading}>
                    <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                    Refresh
                </button>
            </div>

            {/* Main panel */}
            <div className="rounded-xl border border-ink-200/80 bg-white shadow-sm overflow-hidden">
                {/* Tabs */}
                <div className="flex border-b border-ink-100">
                    {TABS.map(t => {
                        const Icon = t.icon;
                        const count = tab === t.id ? stats.all : null;
                        const active = tab === t.id;
                        return (
                            <button
                                key={t.id}
                                type="button"
                                onClick={() => { setTab(t.id); setStatusFilter('all'); setQ(''); }}
                                className={`flex items-center gap-2 px-5 py-3.5 text-sm font-semibold border-b-2 -mb-px transition ${
                                    active
                                        ? 'border-brand-teal text-brand-teal-800'
                                        : 'border-transparent text-ink-500 hover:text-ink-800 hover:border-ink-200'
                                }`}
                            >
                                <Icon className="h-4 w-4" />
                                {t.label}
                                {active && count != null && (
                                    <span className="rounded-full bg-brand-teal/10 px-2 py-0.5 text-[11px] font-bold tabular-nums text-brand-teal-800">
                                        {count}
                                    </span>
                                )}
                            </button>
                        );
                    })}
                </div>

                <div className="p-4 sm:p-5 space-y-4">
                    {/* Status metrics — click to filter (replaces duplicate pill row) */}
                    <div className="flex flex-wrap gap-2">
                        {STAT_KEYS.map(key => {
                            const label = key === 'all' ? 'All' : BUCKET_META[key].label;
                            const dot = key === 'all' ? 'bg-brand-teal' : BUCKET_META[key].dot;
                            const active = statusFilter === key;
                            return (
                                <button
                                    key={key}
                                    type="button"
                                    onClick={() => setStatusFilter(key)}
                                    className={`inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm transition ${
                                        active
                                            ? 'bg-brand-teal-50 text-brand-teal-900 ring-1 ring-brand-teal/25'
                                            : 'bg-ink-50/80 text-ink-600 ring-1 ring-ink-200/60 hover:bg-ink-100'
                                    }`}
                                >
                                    <span className={`h-2 w-2 rounded-full shrink-0 ${dot}`} />
                                    <span className="font-medium">{label}</span>
                                    <span className={`tabular-nums font-bold ${active ? 'text-brand-teal-800' : 'text-ink-800'}`}>
                                        {stats[key]}
                                    </span>
                                </button>
                            );
                        })}
                    </div>

                    {/* Search */}
                    <div className="relative max-w-md">
                        <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-ink-400" />
                        <input
                            className="ag-input pl-9 w-full"
                            placeholder="Search client name…"
                            value={q}
                            onChange={e => setQ(e.target.value)}
                        />
                    </div>

                    {/* Table */}
                    <div className="rounded-lg border border-ink-100 overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm min-w-[640px]">
                                <thead>
                                    <tr className="bg-ink-50 text-left text-[11px] font-semibold uppercase tracking-wide text-ink-500 border-b border-ink-100">
                                        <Th label="Client" sortKey="name" sortBy={sortBy} onSort={setSort} />
                                        {tab === 'agreement' && (
                                            <Th label="Deal" sortKey="deal" sortBy={sortBy} onSort={setSort} align="right" />
                                        )}
                                        <Th label="Collected" sortKey="pct" sortBy={sortBy} onSort={setSort} />
                                        <Th label="Balance" sortKey="balance" sortBy={sortBy} onSort={setSort} align="right" />
                                        <Th label="Stage" sortKey="status" sortBy={sortBy} onSort={setSort} />
                                        <th className="px-4 py-2.5 w-[100px]" />
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-ink-50">
                                    {loading && (
                                        <tr><td colSpan={tab === 'agreement' ? 6 : 5} className="py-12 text-center text-ink-400 text-sm">Loading…</td></tr>
                                    )}
                                    {!loading && view.map(row => (
                                        <tr key={row.id} className="hover:bg-ink-50/50">
                                            <td className="px-4 py-3">
                                                <Link to={`/sales/clients/${row.id}`} className="font-semibold text-ink-900 hover:text-brand-teal-800">
                                                    {row.name}
                                                </Link>
                                                {(row.sub.data?.city || row.sub.data?.officeBranch) && (
                                                    <p className="text-xs text-ink-400 mt-0.5">
                                                        {[row.sub.data?.city, row.sub.data?.officeBranch].filter(Boolean).join(' · ')}
                                                    </p>
                                                )}
                                            </td>
                                            {tab === 'agreement' && (
                                                <td className="px-4 py-3 text-right tabular-nums font-medium text-ink-800">
                                                    {inr(row.deal)}
                                                </td>
                                            )}
                                            <td className="px-4 py-3">
                                                <PaymentBar pct={row.pct} />
                                            </td>
                                            <td className="px-4 py-3 text-right tabular-nums text-ink-700">
                                                {inr(row.balance)}
                                            </td>
                                            <td className="px-4 py-3">
                                                <StageBadge bucket={row.bucket} label={row.stageLabel} />
                                            </td>
                                            <td className="px-4 py-3 text-right">
                                                {tab === 'loi' && row.bucket === 'not_sent' && canUpdateLoi ? (
                                                    <button
                                                        type="button"
                                                        className="ag-btn-primary text-xs py-1.5 px-2.5"
                                                        disabled={busyId === row.id}
                                                        onClick={() => requestLoi(row)}
                                                    >
                                                        <Plus className="h-3.5 w-3.5" /> Request
                                                    </button>
                                                ) : tab === 'agreement' && row.bucket === 'not_sent' && canUpdateAgreement ? (
                                                    <button
                                                        type="button"
                                                        className="ag-btn-primary text-xs py-1.5 px-2.5"
                                                        disabled={busyId === row.id}
                                                        onClick={() => requestAgreement(row)}
                                                    >
                                                        <Plus className="h-3.5 w-3.5" /> Request
                                                    </button>
                                                ) : (
                                                    <Link
                                                        to={`/sales/clients/${row.id}`}
                                                        className="inline-flex items-center gap-1 text-xs font-semibold text-brand-teal-700 hover:text-brand-teal-900"
                                                    >
                                                        <Eye className="h-3.5 w-3.5" /> View
                                                    </Link>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                    {!loading && view.length === 0 && (
                                        <tr>
                                            <td colSpan={tab === 'agreement' ? 6 : 5} className="py-12 text-center">
                                                <p className="text-sm text-ink-500">No clients found.</p>
                                              
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {!loading && view.length > 0 && (
                        <p className="text-xs text-ink-400 text-right tabular-nums">
                            Showing {view.length} of {stats.all}
                        </p>
                    )}
                </div>
            </div>
        </div>
    );
}

function PaymentBar({ pct }) {
    const display = Math.max(0, Number(pct || 0));
    const barWidth = Math.min(100, display);
    const fill = display >= 100 ? 'bg-emerald-500' : display >= 50 ? 'bg-brand-teal' : 'bg-amber-400';
    return (
        <div className="flex items-center gap-2.5 max-w-[160px]">
            <div className="flex-1 h-1.5 rounded-full bg-ink-100 overflow-hidden">
                <div className={`h-full rounded-full ${fill}`} style={{ width: `${barWidth}%` }} />
            </div>
            <span className="text-xs font-semibold tabular-nums text-ink-600 w-14">{display.toFixed(2)}%</span>
        </div>
    );
}

function StageBadge({ bucket, label }) {
    const meta = BUCKET_META[bucket] || BUCKET_META.not_sent;
    return (
        <div className="min-w-0">
            <span className={`inline-flex items-center gap-1.5 rounded-md px-2 py-0.5 text-[11px] font-semibold ring-1 ${meta.chip}`}>
                <span className={`h-1.5 w-1.5 rounded-full shrink-0 ${meta.dot}`} />
                {meta.label}
            </span>
            <p className="text-[11px] text-ink-400 mt-1 truncate max-w-[180px]" title={label}>{label}</p>
        </div>
    );
}

function Th({ label, sortKey, sortBy, onSort, align = 'left' }) {
    const active = sortBy.key === sortKey;
    const Icon = sortBy.dir === 'asc' ? ArrowUp : ArrowDown;
    return (
        <th className={`px-4 py-2.5 ${align === 'right' ? 'text-right' : 'text-left'}`}>
            <button
                type="button"
                onClick={() => onSort(sortKey)}
                className={`inline-flex items-center gap-1 hover:text-ink-700 ${align === 'right' ? 'ml-auto' : ''} ${active ? 'text-ink-800' : ''}`}
            >
                {label}
                {active && <Icon className="h-3 w-3 text-brand-teal" />}
            </button>
        </th>
    );
}
